import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { BarChart3, Gauge } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { shortSha } from "@/lib/format";

/**
 * 006 US3 + US7 — Analytics tab: edge-derived traffic (no client script)
 * plus the Speed Insights section (real-user Core Web Vitals, opt-in).
 *
 * Charts are hand-rolled inline SVG on purpose (no chart dep in this app):
 * a single-series bar chart for traffic over time (single hue = accent
 * token, identity carried by the section title, hover tooltip per bar) and
 * proportional list rows for paths/referrers. All text uses text tokens.
 */

type Range = "24h" | "7d" | "30d";

interface AnalyticsResponse {
  range: Range;
  series: Array<{ t: string; pageviews: number }>;
  totals: {
    pageviews: number;
    visitors: number;
    requests: number;
    bytes: number;
    s4xx: number;
    s5xx: number;
  };
  topPaths: Array<{ path: string; count: number }>;
  topReferrers: Array<{ referrer: string; count: number }>;
  devices: Array<{ name: string; count: number }>;
  browsers: Array<{ name: string; count: number }>;
}

interface SpeedResponse {
  enabled: boolean;
  deployments: Array<{
    deploymentId: string;
    deployment: { id: string; branch: string; commitSha: string; createdAt: string; env: string; isActiveForEnv: boolean } | null;
    metrics: Record<string, { p75: number; samples: number }>;
    samples: number;
    lowSample: boolean;
  }>;
  pages: Array<{ path: string; metric: string; p75: number; samples: number }>;
}

const METRIC_ORDER = ["LCP", "FCP", "INP", "TTFB", "CLS"] as const;

export function Analytics() {
  const { org, project } = useParams<{ org: string; project: string }>();
  const [range, setRange] = useState<Range>("7d");

  const q = useQuery({
    queryKey: ["analytics", org, project, range],
    queryFn: () => api.get<AnalyticsResponse>(`/orgs/${org}/projects/${project}/analytics?range=${range}`),
    refetchInterval: 60_000,
  });
  const speed = useQuery({
    queryKey: ["speed", org, project],
    queryFn: () => api.get<SpeedResponse>(`/orgs/${org}/projects/${project}/speed`),
  });

  const buckets = useMemo(() => bucketize(q.data?.series ?? [], range), [q.data?.series, range]);
  const empty = !q.isLoading && (q.data?.totals.pageviews ?? 0) === 0;

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-lg font-semibold inline-flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-muted-foreground" /> Analytics
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Derived from edge traffic — no tracking script, no cookies. Platform-internal requests are excluded.
          </p>
        </div>
        <div className="flex items-center rounded-md border border-border overflow-hidden shrink-0 self-start sm:self-auto">
          {(["24h", "7d", "30d"] as Range[]).map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={cn(
                "h-8 px-3 text-xs font-medium transition-colors",
                r === range ? "bg-subtle text-foreground" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Pageviews" value={q.data ? fmt(q.data.totals.pageviews) : "—"} />
        <Stat
          label="Visitors"
          value={q.data ? fmt(q.data.totals.visitors) : "—"}
          hint="daily uniques, summed"
        />
        <Stat label="Top path" value={q.data?.topPaths[0]?.path ?? "—"} mono />
        <Stat label="Top referrer" value={q.data?.topReferrers[0]?.referrer ?? "—"} mono />
      </div>

      {/* 007 — full-traffic counters (all non-internal requests, incl. assets/APIs) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Requests" value={q.data ? fmt(q.data.totals.requests) : "—"} hint="all traffic, incl. assets" />
        <Stat label="Bandwidth" value={q.data ? fmtBytes(q.data.totals.bytes) : "—"} hint="response bytes served" />
        <Stat label="4xx" value={q.data ? fmt(q.data.totals.s4xx) : "—"} />
        <Stat
          label="5xx errors"
          value={q.data ? fmt(q.data.totals.s5xx) : "—"}
          hint={
            q.data && q.data.totals.requests > 0
              ? `${((q.data.totals.s5xx / q.data.totals.requests) * 100).toFixed(2)}% of requests`
              : undefined
          }
          danger={Boolean(q.data && q.data.totals.s5xx > 0)}
        />
      </div>

      <div className="of-card overflow-hidden">
        <div className="of-card-header">
          <h3 className="text-sm font-semibold">Pageviews</h3>
          <span className="text-2xs text-subtle-foreground">{range === "24h" ? "per hour" : "per day"} · UTC</span>
        </div>
        <div className="p-4">
          {q.isLoading ? (
            <div className="h-40 grid place-items-center text-sm text-muted-foreground">Loading…</div>
          ) : empty ? (
            <div className="h-40 grid place-items-center text-center text-sm text-muted-foreground">
              <div>
                No traffic recorded in this range yet.
                <div className="text-xs mt-1">Data appears within a minute or two of real visits.</div>
              </div>
            </div>
          ) : (
            <TrafficChart buckets={buckets} />
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RankList title="Top paths" rows={(q.data?.topPaths ?? []).map((p) => ({ name: p.path, count: p.count }))} />
        <RankList
          title="Referrers"
          rows={(q.data?.topReferrers ?? []).map((p) => ({ name: p.referrer, count: p.count }))}
        />
        <RankList title="Devices" rows={q.data?.devices ?? []} />
        <RankList title="Browsers" rows={q.data?.browsers ?? []} />
      </div>

      {/* ── Speed insights ─────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-3 pt-2">
        <div>
          <h2 className="text-base font-semibold inline-flex items-center gap-2">
            <Gauge className="h-4 w-4 text-muted-foreground" /> Speed insights
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Real-user Core Web Vitals (p75), attributed per deployment.
          </p>
        </div>
      </div>

      {!speed.data?.enabled && (
        <div className="of-card px-4 py-3 text-xs text-muted-foreground">
          Speed insights is off for this project. Enable it in{" "}
          <Link to={`/orgs/${org}/projects/${project}/settings`} className="underline hover:text-foreground">
            Settings
          </Link>
          {" "}— static builds get the collector injected automatically; server builds are wrapped at deploy time.
        </div>
      )}

      {(speed.data?.deployments.length ?? 0) === 0 ? (
        speed.data?.enabled ? (
          <div className="of-card px-4 py-8 text-center text-sm text-muted-foreground">
            No samples yet — data appears after real visitors load pages.
          </div>
        ) : null
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {speed.data!.deployments.map((d) => (
            <div key={d.deploymentId} className="of-card">
              <div className="of-card-header">
                <div className="min-w-0 text-xs font-mono truncate">
                  {d.deployment ? (
                    <>
                      {d.deployment.branch} · {shortSha(d.deployment.commitSha)}
                      {d.deployment.isActiveForEnv && (
                        <span className="ml-2 text-2xs uppercase tracking-wider bg-success-subtle text-success border border-success/30 rounded px-1 py-px">
                          live
                        </span>
                      )}
                    </>
                  ) : (
                    shortSha(d.deploymentId, 12)
                  )}
                </div>
                <span className={cn("text-2xs", d.lowSample ? "text-attention" : "text-subtle-foreground")}>
                  {d.samples} samples{d.lowSample ? " — low sample, treat as indicative" : ""}
                </span>
              </div>
              <div className="grid grid-cols-5 divide-x divide-border-muted">
                {METRIC_ORDER.map((m) => (
                  <div key={m} className="px-3 py-2.5 text-center">
                    <div className="text-2xs uppercase tracking-wider text-subtle-foreground">{m}</div>
                    <div className="text-sm font-semibold tabular-nums mt-0.5">
                      {d.metrics[m] ? fmtMetric(m, d.metrics[m]!.p75) : "—"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Traffic bar chart (single series, inline SVG) ────────────────────────

interface Bucket {
  label: string;
  count: number;
}

function TrafficChart({ buckets }: { buckets: Bucket[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const W = 720;
  const H = 160;
  const PAD_B = 18;
  const max = Math.max(1, ...buckets.map((b) => b.count));
  const bw = W / Math.max(1, buckets.length);
  const barW = Math.max(1, bw - 2); // 2px surface gap between bars
  // ~4 recessive gridlines + sparse tick labels.
  const ticks = [0.25, 0.5, 0.75, 1].map((f) => Math.round(max * f));
  const labelEvery = Math.ceil(buckets.length / 6);

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H + PAD_B}`} className="w-full h-44" role="img" aria-label="Pageviews over time">
        {ticks.map((t) => {
          const y = H - (t / max) * H;
          return (
            <g key={t}>
              <line x1={0} x2={W} y1={y} y2={y} className="stroke-border-muted" strokeWidth={1} strokeDasharray="2 4" />
              <text x={W - 2} y={y - 3} textAnchor="end" className="fill-[rgb(var(--of-fg-subtle))]" fontSize={9}>
                {fmt(t)}
              </text>
            </g>
          );
        })}
        <line x1={0} x2={W} y1={H} y2={H} className="stroke-border" strokeWidth={1} />
        {buckets.map((b, i) => {
          const h = Math.max(b.count > 0 ? 2 : 0, (b.count / max) * H);
          return (
            <g key={i}>
              <rect
                x={i * bw + 1}
                y={H - h}
                width={barW}
                height={h}
                rx={Math.min(2, barW / 2)}
                className={cn(
                  "fill-accent transition-opacity",
                  hover !== null && hover !== i ? "opacity-40" : "opacity-90",
                )}
              />
              {/* oversized hit target for hover */}
              <rect
                x={i * bw}
                y={0}
                width={bw}
                height={H}
                fill="transparent"
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
              />
              {i % labelEvery === 0 && (
                <text x={i * bw + bw / 2} y={H + 12} textAnchor="middle" className="fill-[rgb(var(--of-fg-subtle))]" fontSize={9}>
                  {b.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      {hover !== null && buckets[hover] && (
        <div
          className="absolute -top-1 pointer-events-none rounded-md border border-border bg-background px-2 py-1 text-2xs shadow-of-sm"
          style={{ left: `${((hover + 0.5) / buckets.length) * 100}%`, transform: "translateX(-50%)" }}
        >
          <span className="text-subtle-foreground">{buckets[hover].label}</span>{" "}
          <span className="font-semibold tabular-nums">{fmt(buckets[hover].count)}</span>
        </div>
      )}
    </div>
  );
}

function RankList({ title, rows }: { title: string; rows: Array<{ name: string; count: number }> }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="of-card overflow-hidden">
      <div className="of-card-header">
        <h3 className="text-sm font-semibold">{title}</h3>
      </div>
      {rows.length === 0 ? (
        <div className="px-4 py-6 text-center text-xs text-muted-foreground">No data in range.</div>
      ) : (
        <div className="p-2">
          {rows.map((r) => (
            <div key={r.name} className="relative flex items-center justify-between gap-3 px-2 py-1.5 text-xs">
              <div
                className="absolute inset-y-0.5 left-0 bg-accent-subtle rounded-sm"
                style={{ width: `${(r.count / max) * 100}%` }}
              />
              <span className="relative font-mono truncate min-w-0">{r.name}</span>
              <span className="relative tabular-nums text-muted-foreground shrink-0">{fmt(r.count)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
  mono,
  danger,
}: {
  label: string;
  value: string;
  hint?: string;
  mono?: boolean;
  danger?: boolean;
}) {
  return (
    <div className="of-card px-4 py-3">
      <div className="text-2xs uppercase tracking-wider text-subtle-foreground">{label}</div>
      <div
        className={cn(
          "text-lg font-semibold mt-0.5 truncate",
          mono && "font-mono text-sm leading-7",
          danger && "text-danger",
        )}
      >
        {value}
      </div>
      {hint && <div className="text-2xs text-subtle-foreground">{hint}</div>}
    </div>
  );
}

function fmtBytes(n: number): string {
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1)}GB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1)}MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(1)}kB`;
  return `${n}B`;
}

// ── helpers ──────────────────────────────────────────────────────────────

function bucketize(series: Array<{ t: string; pageviews: number }>, range: Range): Bucket[] {
  if (range === "24h") {
    // Continuous hourly buckets, zero-filled.
    const byHour = new Map(series.map((s) => [new Date(s.t).setMinutes(0, 0, 0), s.pageviews]));
    const out: Bucket[] = [];
    const now = new Date();
    now.setMinutes(0, 0, 0);
    for (let i = 23; i >= 0; i--) {
      const t = new Date(now.getTime() - i * 3600_000);
      out.push({
        label: `${String(t.getUTCHours()).padStart(2, "0")}:00`,
        count: byHour.get(t.getTime()) ?? 0,
      });
    }
    return out;
  }
  const days = range === "7d" ? 7 : 30;
  const byDay = new Map<string, number>();
  for (const s of series) {
    const d = new Date(s.t).toISOString().slice(0, 10);
    byDay.set(d, (byDay.get(d) ?? 0) + s.pageviews);
  }
  const out: Bucket[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(Date.now() - i * 86400_000).toISOString().slice(0, 10);
    out.push({ label: d.slice(5), count: byDay.get(d) ?? 0 });
  }
  return out;
}

function fmt(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

function fmtMetric(metric: string, v: number): string {
  if (metric === "CLS") return v.toFixed(2);
  if (v >= 1000) return `${(v / 1000).toFixed(2)}s`;
  return `${Math.round(v)}ms`;
}
